/**
 * JsonDiff — before/after key-level diff for an audit row (STORY-008-008).
 * Pure render over two JSON objects; values are JSON.stringify-rendered. Keys
 * are unioned; each row is added / removed / changed / unchanged. No copy here
 * is user-data — labels come from i18n in the caller.
 */
import { useTranslation } from "react-i18next";
import { type ReactElement } from "react";
import { ADMIN_NS } from "@/features/admin/api";

export type DiffKind = "added" | "removed" | "changed" | "unchanged";

export interface DiffRow {
  key: string;
  kind: DiffKind;
  before: unknown;
  after: unknown;
}

type JsonObj = Record<string, unknown> | null;

/**
 * Stable JSON stringify: recursively sorts object keys so that two structurally
 * equal values with differently-ordered nested keys serialize identically.
 * Plain `JSON.stringify` is key-order sensitive, which made reordered-but-equal
 * nested objects show up as "changed". Arrays keep their order (order matters).
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const obj = value as Record<string, unknown>;
  const entries = Object.keys(obj)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`);
  return `{${entries.join(",")}}`;
}

export function diffRows(before: JsonObj, after: JsonObj): DiffRow[] {
  const b = before ?? {};
  const a = after ?? {};
  const keys = Array.from(new Set([...Object.keys(b), ...Object.keys(a)])).sort();
  return keys.map((key): DiffRow => {
    const inB = key in b;
    const inA = key in a;
    const bv = b[key];
    const av = a[key];
    let kind: DiffKind;
    if (inB && !inA) kind = "removed";
    else if (!inB && inA) kind = "added";
    else kind = stableStringify(bv) === stableStringify(av) ? "unchanged" : "changed";
    return { key, kind, before: inB ? bv : undefined, after: inA ? av : undefined };
  });
}

function fmt(v: unknown): string {
  if (v === undefined) return "—";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

const KIND_TONE: Record<DiffKind, string> = {
  added: "text-[var(--g-success)]",
  removed: "text-[var(--g-danger)]",
  changed: "text-[var(--g-primary)]",
  unchanged: "text-[var(--g-secondary)]",
};

export function JsonDiff({
  before,
  after,
}: {
  before: JsonObj;
  after: JsonObj;
}): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const rows = diffRows(before, after);
  if (rows.length === 0) {
    return (
      <div data-testid="audit-diff-empty" className="text-sm text-[var(--g-secondary)]">
        {t("audit.diff.empty")}
      </div>
    );
  }
  return (
    <div data-testid="audit-diff" className="flex flex-col gap-1">
      <div className="grid grid-cols-[1fr_1fr] gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
        <span>{t("audit.diff.before")}</span>
        <span>{t("audit.diff.after")}</span>
      </div>
      {rows.map((r) => (
        <div
          key={r.key}
          data-testid={`audit-diff-row-${r.key}`}
          data-kind={r.kind}
          className="rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] p-2"
        >
          <div className={`text-xs font-medium ${KIND_TONE[r.kind]}`}>{r.key}</div>
          <div className="grid grid-cols-[1fr_1fr] gap-2 text-sm">
            <code className="break-words text-[var(--g-secondary)] line-through decoration-[var(--g-hairline)]">
              {fmt(r.before)}
            </code>
            <code className="break-words">{fmt(r.after)}</code>
          </div>
        </div>
      ))}
    </div>
  );
}

export default JsonDiff;
